import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { unstable_usePrompt, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { CenterFlex, Divider, VFlex } from '@/components/base';
import Spinner from '@/components/Common/Spinner';
import { Stage } from '@/components/Stage/Stage';
import { usePublic } from '@/contexts/PublicContext';

import { reportService } from '../api';
import ReportDetailBody from '../components/ReportDetail/ReportDetailBody';
import ReportDetailHeader from '../components/ReportDetail/ReportDetailHeader';
import useReportStore from '../store/ReportStore';

const ReportDetailPage = () => {
  const isPublic = usePublic();
  const { report_alias } = useParams();
  if (!report_alias) throw new Error('report_alias is required');
  const report = useReportStore((state) => state.report);
  const reportBackup = useReportStore((state) => state.reportBackup);
  const setReport = useReportStore((state) => state.setReport);
  const clearReport = useReportStore((state) => state.clearReport);
  const mode = useReportStore((state) => state.mode);

  const isReportEditMode = mode === 'EDIT';

  const { data, isLoading: isLoadingReport } = useQuery({
    queryKey: ['report', report_alias, isPublic],
    queryFn: () => reportService.fetchReport(report_alias, isPublic),
  });

  useEffect(() => {
    if (data) {
      setReport(data);
    }
  }, [data, setReport]);

  useEffect(() => {
    return () => {
      clearReport();
    };
  }, [clearReport]);

  unstable_usePrompt({
    when:
      isReportEditMode &&
      JSON.stringify(reportBackup) !== JSON.stringify(report),
    message: 'Changes you made may not be saved.',
  });

  return (
    <Style>
      <Stage>
        <Stage.Scene status={isLoadingReport}>
          <CenterFlex height="600px">
            <Spinner />
          </CenterFlex>
        </Stage.Scene>
        <Stage.Scene status={!!report} data={report}>
          {(report) => (
            <Style f={1}>
              <ReportDetailHeader report={report} />
              <Divider />
              <ReportDetailBody report={report} />
            </Style>
          )}
        </Stage.Scene>
      </Stage>
    </Style>
  );
};

const Style = styled(VFlex)`
  flex: 1;
  overflow: auto;
  height: 100vh;
`;

export default ReportDetailPage;
